import { defineComponent as _defineComponent } from 'vue'
import { useRouter } from '@/composables/router';
import { localStorageService } from '@/library-services/localStorage.service';
import { WorkCenterConsoleOverviewStateDataRetriever } from '@/models/Charts/workCenterConsoleOverviewStateDataRetriever';
import { Device } from '@/models/device';
import {
  BendDeviceStateData,
  LaserTubeDeviceStateData,
} from '@/models/deviceStateData';
import { BetaFeature } from '@/models/enums/BetaFeature';
import { FilterTimeSpanEnum } from '@/models/enums/FilterTimeSpanEnum';
import { MachineType } from '@/models/enums/MachineType';
import { Routes } from '@/router/routes';
import { devicesService } from '@/services/devices.service';
import { locationsService } from '@/services/locations.service';
import { shiftsService } from '@/services/shifts.service';
import { customersService } from '@/services/tenants.service';
import { Logger } from '@/utils/logger';
import { isEmpty, isNil } from '@/utils/misc';
import { versionIsGreaterThanOrEqualTo } from '@/utils/version';
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import TimeSpanFilter from '@/components/TimeSpanFilter.vue';
import ShiftFilter from '@/components/ShiftFilter.vue';
import OverviewBendDeviceCard from '@/components/workCenterConsole/OverviewBendDeviceCard.vue';
import OverviewNewBendDeviceCard from '@/components/workCenterConsole/OverviewNewBendDeviceCard.vue';
import OverviewLaserTubeDeviceCard from '@/components/workCenterConsole/OverviewLaserTubeDeviceCard.vue';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import { useInterval } from '@/composables/misc';
import { currentCustomer, currentUser } from '@/utils/permissionUtils';
import WorkCenterConsoleOverviewBendingDevicesStateDataRetriever from '@/models/Charts/workCenterConsoleOverviewBendingDevicesStateDataRetriever';
import { Customer } from '@/models/tenant';
import { useI18n } from '@/composables/i18n';
import { RequestCanceledError } from '@/services/requestCanceledError';


/** @deprecated THIS COMPONENT IS FROZEN **/
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'WorkCenterConsoleOverview',
  setup(__props) {

const compactMode = ref(localStorageService.compactMode);
provide('compactMode', compactMode);

const now = ref(new Date());
provide('now', now);

const loading = ref(false);
provide('loading', loading);

const isFirstLoad = ref(true);
provide('isFirstLoad', isFirstLoad);

const laserTubeDeviceStates = ref<LaserTubeDeviceStateData[]>([]);
const bendDeviceStates = ref<
  LaserTubeDeviceStateData[] | BendDeviceStateData[]
>([]);

const selectedTimespan = ref(FilterTimeSpanEnum.Day);
const selectedShiftIds = ref<number[]>([]);

const statesDataRetriever = new WorkCenterConsoleOverviewStateDataRetriever(
  WidgetEnum.OverviewData,
  currentCustomer()!.tenantIdDh,
);
const bendingDevicesStatesDataRetriever =
  new WorkCenterConsoleOverviewBendingDevicesStateDataRetriever(
    WidgetEnum.OverviewData,
    currentCustomer()!.tenantIdDh,
  );

const router = useRouter();
const { t } = useI18n();

useInterval(updateStatuses, 30);
useInterval(() => (now.value = new Date()), 1);

onMounted(() => {
  // This mustn't be awaited. Otherwise, the setInterval calls might be done
  // after the clean-up in beforeDestroy. This can happen if the user
  // navigates away before this call returns.
  updateStatuses(true);
});

async function updateStatuses(isFirstExec = false) {
  statesDataRetriever.abort();
  bendingDevicesStatesDataRetriever.abort();
  loading.value = true;

  try {
    laserTubeDeviceStates.value = await retrieveLaserTubeDeviceStates(
      isFirstExec,
    );
    bendDeviceStates.value = await retrieveBendDeviceStates(isFirstExec);
    loading.value = false;
  } catch (e) {
    if (!(e instanceof RequestCanceledError)) {
      loading.value = false;
    }
  } finally {
    isFirstLoad.value = false;
  }

  if (isFirstExec) {
    updateStatuses();
  }
}

async function retrieveLaserTubeDeviceStates(
  isFirstExec: boolean,
): Promise<LaserTubeDeviceStateData[]> {
  return await retrieveStatesData(laserTubeDeviceIds.value, isFirstExec);
}

async function retrieveBendDeviceStates(
  isFirstExec: boolean,
): Promise<LaserTubeDeviceStateData[] | BendDeviceStateData[]> {
  if (shouldShowNewBendDeviceCard.value) {
    return await bendingDevicesStatesDataRetriever.getData(
      bendDeviceIds.value,
      selectedShiftIds.value,
      selectedTimespan.value,
    );
  }
  return await retrieveStatesData(bendDeviceIds.value, isFirstExec);
}

async function retrieveStatesData(
  deviceIds: string[],
  isFirstExec: boolean,
): Promise<LaserTubeDeviceStateData[]> {
  return await statesDataRetriever.getData(
    deviceIds,
    selectedShiftIds.value,
    selectedTimespan.value,
    undefined,
    {
      statesOnly: isFirstExec,
    },
  );
}

// FIXME: Code duplication with Chart.onClickEvent
function goToDevice(device: Device) {
  let route: Routes;

  switch (device.getType()) {
    case MachineType.Cut:
      route = Routes.Cut;
      break;
    case MachineType.Bend:
      route = Routes.Bend;
      break;
    case MachineType.Tube:
      route = Routes.Tubes;
      break;
    default:
      Logger.error('Wrong device type', device.getType());
      return;
  }

  router.push({
    name: route,
    // FIXME: Use URL query parameters instead
    params: {
      selectedDeviceStringId: device.deviceId,
    },
  });
}

function getLaserTubeDeviceState(
  device: Device,
): LaserTubeDeviceStateData | undefined {
  return laserTubeDeviceStates.value.find(
    (state) => state.deviceStringId === device.deviceId,
  );
}

function getBendDeviceState(
  device: Device,
): LaserTubeDeviceStateData | BendDeviceStateData | undefined {
  return bendDeviceStates.value.find(
    (state) => state.deviceStringId === device.deviceId,
  );
}

function getLocationName(device: Device): string {
  return locationsService.store.findById(device.locationId)!.name;
}

function getShiftName(shiftId: number): string {
  return shiftsService.store.getNameById(shiftId);
}

watch(compactMode, () => {
  localStorageService.compactMode = compactMode.value;
});

watch(
  () => currentUser().isBetaEnabled,
  () => {
    isFirstLoad.value = true;
    laserTubeDeviceStates.value = [];
    bendDeviceStates.value = [];
    updateStatuses();
  },
);

const devices = computed<Device[]>(() => {
  if (isEmpty(locationsService.store.getAll())) {
    return [];
  }
  return devicesService.store
    .getAll()
    .sort(
      (a: Device, b: Device) =>
        (getLocationName(a).localeCompare(getLocationName(b)) ?? 0) ||
        a.deviceId[0].localeCompare(b.deviceId[0]) ||
        a.name.localeCompare(b.name),
    );
});

const devicesInSelectedShifts = computed<Device[]>(() => {
  if (isEmpty(selectedShiftIds.value)) {
    return devices.value;
  }
  const shifts = shiftsService.store.getMatchingIds(selectedShiftIds.value);
  const relatedDevices = shifts.flatMap((shift) => shift.relatedDevices);
  return devices.value.filter((device) => relatedDevices.includes(device.id));
});

const shouldShowNewBendDeviceCard = computed<boolean>(
  () =>
    !isNil(customer.value.maxDataPlatformVersion) &&
    versionIsGreaterThanOrEqualTo(
      '1.3.0',
      customer.value.maxDataPlatformVersion,
    ) &&
    currentUser().hasBetaFeature(BetaFeature.WorkCenterConsoleBend),
);

const deviceIds = computed<string[]>(() =>
  devices.value.map((device) => device.deviceId),
);

const bendDeviceIds = computed<string[]>(() =>
  devicesService.store.getAllBendDevices().map((device) => device.deviceId),
);

const laserTubeDeviceIds = computed<string[]>(() =>
  [
    ...devicesService.store.getAllCutDevices(),
    ...devicesService.store.getAllTubeDevices(),
  ].map((device) => device.deviceId),
);

const customer = computed<Customer>(() => customersService.store.current()!);

onBeforeUnmount(() => {
  statesDataRetriever.abort();
});

return { __sfc: true,compactMode, now, loading, isFirstLoad, laserTubeDeviceStates, bendDeviceStates, selectedTimespan, selectedShiftIds, statesDataRetriever, bendingDevicesStatesDataRetriever, router, t, updateStatuses, retrieveLaserTubeDeviceStates, retrieveBendDeviceStates, retrieveStatesData, goToDevice, getLaserTubeDeviceState, getBendDeviceState, getLocationName, getShiftName, devices, devicesInSelectedShifts, shouldShowNewBendDeviceCard, deviceIds, bendDeviceIds, laserTubeDeviceIds, customer, isEmpty, TimeSpanFilter, ShiftFilter, OverviewBendDeviceCard, OverviewNewBendDeviceCard, OverviewLaserTubeDeviceCard }
}

})